import { FunctionNodeModel } from "@/models/nodes/FunctionNodeModel";
import { InlineNodeModel } from "@/models/nodes/InlineNodeModel";
import { NumberNodeModel } from "@/models/nodes/NumberNodeModel";
import { WrapperNodeModel } from "@/models/nodes/WrapperNodeModel";

import { getContextId, getNodeLevel } from "./utils";

import type { NodeModel } from "@/models/nodes/types";

export type CursorDirection = "left" | "right";

type CursorPosition = { node: InlineNodeModel; index: number };

/**
 * Most nodes use tree order,
 * but postfix functions and numbers with power+prefix unit are reversed.
 */
function getNavigationChildren(node: NodeModel): NodeModel[] {
  if (node.is(FunctionNodeModel) && node.placement === "postfix") {
    return [...node.childrenPlease].reverse();
  }

  if (node.is(NumberNodeModel) && node.power && node.unitNode?.placement === "prefix") {
    return [...node.childrenPlease].reverse();
  }

  return node.childrenPlease;
}

function inlineMatchesVisualCriteria(
  inline: InlineNodeModel,
  targetContextId: string,
  targetLevel: number,
  direction: CursorDirection,
): boolean {
  const ctx = getContextId(inline);
  const lvl = getNodeLevel(inline);

  if (ctx === targetContextId || lvl < targetLevel) {
    return true;
  }

  /**
   * Special case moving RIGHT only: same depth with no context (top-level root body).
   * Without this, moving right out of a fraction denominator would get stuck and never
   * reach the expression to the right of the whole fraction.
   */
  if (direction === "right" && lvl === targetLevel && !ctx) {
    return true;
  }

  return false;
}

function findMatchingDescendant(
  node: NodeModel,
  contextId: string,
  level: number,
  direction: CursorDirection,
): InlineNodeModel | undefined {
  if (node.isAtom) {
    return undefined;
  }

  if (node.is(InlineNodeModel)) {
    return inlineMatchesVisualCriteria(node, contextId, level, direction) ? node : undefined;
  }

  const children = getNavigationChildren(node);
  const traversedChildren = direction === "right" ? children : [...children].reverse();

  for (const child of traversedChildren) {
    const found = findMatchingDescendant(child, contextId, level, direction);
    if (found) return found;
  }

  return undefined;
}

/** The cursor index at the near edge of `inline` for the given direction. */
function enterInline(inline: InlineNodeModel, direction: CursorDirection): CursorPosition {
  return { node: inline, index: direction === "right" ? 0 : inline.nodes.length };
}

export function findCursorPosition(
  inlineNode: InlineNodeModel,
  index: number,
  direction: CursorDirection,
): CursorPosition | undefined {
  const contextId = getContextId(inlineNode);
  const level = getNodeLevel(inlineNode);
  const step = direction === "right" ? 1 : -1;

  const atBoundary = direction === "right" ? index >= inlineNode.nodes.length : index <= 0;

  if (!atBoundary) {
    // The node the cursor would step across: ahead of it moving right, behind it
    // moving left.
    const child = inlineNode.nodes[direction === "right" ? index : index - 1];
    const matching = findMatchingDescendant(child, contextId, level, direction);

    if (matching) {
      return enterInline(matching, direction);
    }

    return { node: inlineNode, index: index + step };
  }

  let current: NodeModel = inlineNode;

  while (current.parent) {
    if (current.parent.is(WrapperNodeModel)) return undefined;

    const parent = current.parent as NodeModel;
    const navChildren = getNavigationChildren(parent);
    const myIndex = navChildren.indexOf(current);

    if (parent.is(InlineNodeModel)) {
      if (inlineMatchesVisualCriteria(parent, contextId, level, direction)) {
        return {
          node: parent,
          index: direction === "right" ? current.index + 1 : current.index,
        };
      }
    }

    const traversedChildren =
      direction === "right"
        ? navChildren.slice(myIndex + 1)
        : navChildren.slice(0, myIndex).reverse();

    for (const child of traversedChildren) {
      const matching = findMatchingDescendant(child, contextId, level, direction);
      if (matching) return enterInline(matching, direction);
    }

    current = parent;
  }

  return undefined;
}
